import { debounce, get, isArray } from 'lodash';

import React, {
    useState,
    useRef,
    useEffect,
    useContext,
    useMemo,
    useCallback,
} from 'react';
import PropTypes from 'prop-types';
import {
    EditorState,
    RichUtils,
    ContentState, CompositeDecorator,
} from 'draft-js';
import Editor from '@draft-js-plugins/editor';
import { stateToHTML } from 'draft-js-export-html';
import { Resizable } from 're-resizable';
import InputLabel from '@material-ui/core/InputLabel';
import htmlToDraft from 'html-to-draftjs';
import ApplicationEventsContext from '../../contexts/ApplicationEventsContext';
import useStyles from './TextEditorStyles';
import Toolbar from './textEditorHeader/Toolbar';
import Utils from '../../services/UtilsService';
import TextService from '../../services/TextService';
import createLinkPlugin from './plugins/link/LinkPlugin';

export const stripHtmlFromText = (html) => {
    if (!html) {
        return '';
    }

    const div = document.createElement('div');

    div.innerHTML = html;

    return get(div, 'textContent', '').trim() || get(div, 'innerText', '').trim() || '';
};

const linkPlugin = createLinkPlugin({ disabledLink: true });

// Modified by AI on 05/26/2026. Edit #1.
const TextEditor = ({
    lines = 5,
    lineHeight = 24,
    testId = 'text-editor',
    labelText = '',
    className = '',
    placeHolder = '',
    required = false,
    error = false,
    value = '',
    onChange = () => {},
    settings = {
        showLabel: true,
        showToolBar: true,
        toolBarAlignment: 'top',
        renderToolBar: null,
        resizable: {
            top: false,
            right: false,
            bottom: true,
            left: false,
            topRight: false,
            bottomRight: false,
            bottomLeft: false,
            topLeft: false,
        },
    },
    disableFocus = false,
    fullHeight = false,
    plugins = [linkPlugin],
    appEventForExternalValueChange = null,
    onFocus: onFocusHandler = () => {},
    onBlur: onBlurHandler = () => {},
    readOnly,
}) => {
    const { subscribe, unsubscribe } = useContext(ApplicationEventsContext);

    const resultDecorator = useMemo(() => {
        const decorators = plugins
            .filter((plugin) => isArray(plugin.decorators))
            .reduce((resultDecorators, plugin) => [...resultDecorators, ...plugin.decorators], []);

        return new CompositeDecorator(decorators);
    }, [plugins]);

    const [editorState, setEditorState] = useState(EditorState.createEmpty(resultDecorator));
    const [maxHeight, setMaxHeight] = useState();
    const [isEditorFocused, setEditorFocus] = useState(false);

    const onChangeInternal = useCallback((newState) => {
        const currentContent = newState.getCurrentContent();
        const html = stateToHTML(currentContent);

        const contentHtml = TextService.sanitize(html).replace(/(\r\n|\n\r|\r|\n)/g, '');
        const strippedText = stripHtmlFromText(contentHtml);

        if (value === strippedText) {
            return;
        }

        const isEmptyString = !(strippedText || '').trim();
        if (isEmptyString) {
            if (editorState.getCurrentContent().hasText()) {
                setEditorState(newState);
                onChange('');
            }
        } else {
            setEditorState(newState);
            onChange(contentHtml);
        }
    }, [value, onChange]);

    const onFocus = () => {
        setEditorFocus(true);

        onBlurHandler(editorState, setEditorState);
    };

    const onBlur = () => {
        setEditorFocus(false);

        onFocusHandler(editorState, setEditorState, onChangeInternal);
    };

    const classes = useStyles({
        lines,
        error,
        maxHeight,
        fullHeight,
        disableFocus,
        lineHeight,
    });
    const editor = useRef(null);

    const toggleBlockType = (blockType) => {
        const newState = RichUtils.toggleBlockType(editorState, blockType);
        const contentHtml = TextService.sanitize(stateToHTML(newState.getCurrentContent()));

        setEditorState(newState);
        onChange(contentHtml);
    };

    const toggleInlineType = (blockType) => {
        const newState = RichUtils.toggleInlineStyle(editorState, blockType);
        const contentHtml = TextService.sanitize(stateToHTML(newState.getCurrentContent()));

        setEditorState(newState);
        onChange(contentHtml);
    };

    const focusEditor = () => {
        editor.current.focus();
        onFocus();
    };

    const handleKeyCommand = (command) => {
        if (['bold', 'underline', 'italic'].indexOf(command) !== -1) {
            onChangeInternal(RichUtils.toggleInlineStyle(editorState, command.toUpperCase()));

            return 'handled';
        }

        return 'not-handled';
    };

    const onResize = debounce((event, direction, refToElement) => {
        setMaxHeight(refToElement.offsetHeight);
    }, 20);

    const containerClassName = Utils.getClassName([classes.container, className]);
    const labelClassName = Utils.getClassName([classes.labelText, 'labelText']);
    const editorContainerClassName = Utils.getClassName([classes.editorContainer]);
    const editorClassName = Utils.getClassName([classes.editor]);

    // Hack! DraftJs has issues with external text change.
    // After each change cursor is moving to the begging of the text.
    // Here component subscribes for each external change
    useEffect(() => {
        if (!appEventForExternalValueChange) {
            return () => {};
        }

        const externalValuesChangeListener = (event) => {
            const { contentBlocks, entityMap } = htmlToDraft(event.data);
            const contentState = ContentState.createFromBlockArray(contentBlocks, entityMap);

            setEditorState(EditorState.createWithContent(contentState, resultDecorator));
        };

        subscribe(appEventForExternalValueChange, externalValuesChangeListener);

        return () => unsubscribe(externalValuesChangeListener);
    }, [resultDecorator, appEventForExternalValueChange]);

    const toolbarProps = {
        className: 'editor-toolbar',
        alignment: settings.toolBarAlignment,
        editorState,
        setEditorState,
        onToggleInline: toggleInlineType,
        onToggleBlock: toggleBlockType,
        onChangeInternal,
    };

    if (settings.resizable === false) {
        // eslint-disable-next-line no-param-reassign
        settings.resizable = {
            top: false,
            right: false,
            bottom: false,
            left: false,
            topRight: false,
            bottomRight: false,
            bottomLeft: false,
            topLeft: false,
        };
    }

    const EditorContainer = <div
        className={editorClassName}
        {...(readOnly && { onClickCapture: (e) => e.stopPropagation() })}
    >
        <Editor
            ref={editor}
            plugins={plugins}
            onBlur={onBlur}
            editorState={editorState}
            onChange={onChangeInternal}
            handleKeyCommand={handleKeyCommand}
            placeholder={isEditorFocused ? null : placeHolder}
            readOnly={readOnly}
        />
    </div>;

    return (
        <div
            className={containerClassName}
            onClick={focusEditor}
            onContextMenu={focusEditor}
            data-test-id={testId}
        >
            { settings.showLabel && <InputLabel
                data-test-id='input-label'
                required={required}
                shrink={true}
                className={labelClassName}
                classes={{ shrink: classes.labelShrink }}
            >
                <span>{labelText}</span>
            </InputLabel> }
            <div className={editorContainerClassName}>
                {(() => {
                    if (!settings.showToolBar || settings.toolBarAlignment !== 'top') {
                        return null;
                    }

                    if (typeof settings.renderToolBar === 'function') {
                        return settings.renderToolBar(toolbarProps);
                    }

                    return <Toolbar {...toolbarProps}/>;
                })()}
                { !fullHeight && <Resizable
                    enable={settings.resizable}
                    onResize={onResize}
                    handleClasses={{ bottom: classes.resizeHandle }}
                    snapGap={3}
                >
                    {EditorContainer}
                </Resizable> }
                { fullHeight && EditorContainer }
                {(() => {
                    if (!settings.showToolBar || settings.toolBarAlignment !== 'bottom') {
                        return null;
                    }

                    if (typeof settings.renderToolBar === 'function') {
                        return settings.renderToolBar(toolbarProps);
                    }

                    return <Toolbar {...toolbarProps}/>;
                })()}
            </div>
        </div>
    );
};

TextEditor.propTypes = {
    testId: PropTypes.string,
    lines: PropTypes.number,
    lineHeight: PropTypes.number,
    labelText: PropTypes.string,
    className: PropTypes.string,
    placeHolder: PropTypes.string,
    fullHeight: PropTypes.bool,
    required: PropTypes.bool,
    error: PropTypes.bool,
    value: PropTypes.string,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
    settings: PropTypes.object,
    disableFocus: PropTypes.bool,
    decorator: PropTypes.object,
    appEventForExternalValueChange: PropTypes.string,
};

export default TextEditor;
